/**
 * **I dati di fatturazione di Beste**, e il QR che li contiene. Zero import.
 *
 * ## Un padrone solo: il payload
 *
 * Il fatto autorevole è **la stringa del QR**, e non i campi: è quella che un fornitore inquadra
 * col proprio gestionale, e da quella si riempiono i suoi campi. Se a schermo scrivessimo i numeri
 * a mano accanto a un QR generato altrove, il giorno che la sede cambia ne cambierebbe **uno dei
 * due** — e nessuno se ne accorgerebbe, perché a schermo si legge il testo e a inquadrare è la
 * macchina del fornitore. Qui i campi si **leggono dal payload**: non possono divergere.
 *
 * ## Che cos'è questa stringa
 *
 * È il **QR-Code fattura** dell'Agenzia delle Entrate: il formato che le aziende italiane
 * stampano sui biglietti da visita e sulle app perché chi emette una fattura non debba digitare
 * partita IVA, indirizzo e recapito elettronico. Il contenuto l'ha dato Cosimo il 2026-10-05,
 * fotografando il QR ufficiale di Beste; è stato **letto dalla fotografia**, non ricopiato a mano,
 * e il QR che l'app disegna è rigenerato da questa stessa stringa (`fai-qr-fatturazione.py`).
 *
 * ⚠ **`dtGenQr` è del 2022 e si lascia com'è.** È la data in cui l'Agenzia ha generato quel QR, e
 * fa parte del payload firmato dal formato: cambiarla vorrebbe dire inventare un dato dentro una
 * stringa che qualcun altro legge come ufficiale. Se un giorno Beste cambia sede o PEC, non si
 * modifica un campo qui: si **rigenera il QR** dal portale dell'Agenzia e si sostituisce la
 * stringa intera.
 *
 * ⚠ **Il codice destinatario `0000000` non è un errore e non è un segnaposto**: sette zeri vogliono
 * dire «questa azienda riceve le fatture elettroniche via **PEC**, non via codice SDI». Chi lo
 * legge a schermo deve capirlo, o proverà a cercare il codice vero.
 */
/**
 * Il payload del QR, esattamente com'è. Non si riformatta, non si riordina, non si abbellisce: è
 * il testo che il lettore del fornitore si aspetta, carattere per carattere.
 */
export const FATTURAZIONE_QR = '{"anag":{"naz":"IT","cf":"01629440973","piva":"01629440973","denom":"BESTE SPA",' +
    '"domFisc":{"prov":"PO","cap":"59025","com":"CANTAGALLO","ind":"VIA PRIMO LEVI 6","naz":"IT"}},' +
    '"dtGenQr":"2022-01-20T15:21:48.577+0100","SDI":{"pec":"fatture@beste-pec.it","cod":"0000000"}}';
/**
 * I campi, letti dal payload.
 *
 * Si legge **all'avvio del modulo** e non a ogni apertura della schermata: la stringa è una
 * costante, e rifare il lavoro a ogni disegno sarebbe lavoro per niente. Se un giorno la stringa
 * fosse scritta male, questa riga scoppierebbe subito invece di scoppiare in trasferta davanti al
 * fornitore — ed è il verso giusto dell'errore. A impedirlo c'è
 * `packages/shared/scripts/prova-fatturazione.mjs`, che la legge e la confronta coi valori attesi.
 */
function leggiIlPayload(testo) {
    const q = JSON.parse(testo);
    const codiceSdi = q.SDI.cod;
    return {
        denominazione: q.anag.denom,
        partitaIva: q.anag.piva,
        codiceFiscale: q.anag.cf,
        unNumeroSolo: q.anag.piva === q.anag.cf,
        indirizzo: q.anag.domFisc.ind,
        cap: q.anag.domFisc.cap,
        comune: q.anag.domFisc.com,
        provincia: q.anag.domFisc.prov,
        pec: q.SDI.pec,
        codiceSdi,
        perPec: /^0+$/.test(codiceSdi),
    };
}
/** I dati di fatturazione di Beste, già letti. */
export const FATTURAZIONE = leggiIlPayload(FATTURAZIONE_QR);
/**
 * L'indirizzo in una riga sola: «VIA PRIMO LEVI 6 — 59025 CANTAGALLO (PO)».
 *
 * Si compone qui e non nelle schermate perché lo scrivono in due — l'app e la guida ai dipendenti
 * — e due composizioni della stessa riga sono due punteggiature che un giorno non coincidono.
 */
export function indirizzoDiFatturazione(d = FATTURAZIONE) {
    return `${d.indirizzo} — ${d.cap} ${d.comune} (${d.provincia})`;
}
//# sourceMappingURL=fatturazione.js.map